"use client";

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams } from "@/hooks";
import { AppDispatch } from "@/store";
import { useDispatch, useSelector } from "react-redux";
import { usePermission } from "@/hooks/usePermission/usePermission";
import {
  fetchServices,
  createService,
  updateService,
  deleteService,
  fetchServiceByClient,
  uploadServiceImage,
} from "@/store/modules/services/thunks";
import {
  fetchCategory,
  createCategory,
  updateCategory,
  deleteCategory,
} from "@/store/modules/categories/thunks";
import { fetchEmployees } from "@/store/modules/employees/thunks";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { serviceSchema, categorySchema } from "@/validations";
import { useAuth } from "@/context/AuthContext";
import {
  CreateServiceData,
  UpdateServiceData,
  ServiceSlugState,
} from "@/types";
import { resetFetched } from "@/store/modules/services/slice";
import { resetFetched as resetCategoriesFetched } from "@/store/modules/categories/slice";
import { useApiReady } from "@/hooks/useApiReady";

const useService = (onSuccess?: () => void) => {
  const { params, setParams, handlePage, updateParams } = useParams();
  const { per_page, current_page } = params;
  const { hasPermission, loading: loadingPermission } = usePermission();
  const { currency } = useAuth();
  const { isReady: isApiReady } = useApiReady();

  const dispatch: AppDispatch = useDispatch();
  const { services, loading, error, fetched } = useSelector(
    (state: any) => state.services,
  );
  const { categories, fetched: categoriesFetched } = useSelector(
    (state: any) => state.categories,
  );
  const { employees, fetched: employeesFetched } = useSelector(
    (state: any) => state.employees,
  );

  const [serviceSlug, setServiceSlug] = useState<any>();
  const [categorySlug, setCategorySlug] = useState<string | null>(null);
  // Imagem selecionada antes da criação
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [searchValue, setSearchValue] = useState<string>(params.search || "");
  // Flag para indicar remoção da imagem existente sem envio de nova
  const [removeExistingImage, setRemoveExistingImage] =
    useState<boolean>(false);

  // Cache inteligente - previne requisições duplicadas
  const [hasInitialized, setHasInitialized] = useState(false);

  // CARREGAMENTO ÚNICO - EXECUTA APENAS UMA VEZ
  useEffect(() => {
    if (!isApiReady) return;

    // Só carrega se não inicializou, não está carregando, tem permissão E não tem dados suficientes
    const shouldLoad =
      !hasInitialized &&
      !loading &&
      hasPermission("read service") &&
      (!services?.data || services.data.length === 0);

    if (shouldLoad) {
      const loadData = async () => {
        console.log("🚀 Service hook: Carregamento único...");
        const requests = [];

        // Services
        if (!fetched) {
          // Garante paginação de 10 por página na tela de serviços
          const serviceParams = {
            per_page: 200,
            current_page: 1,
            without_pagination: 0,
            search: params.search || "",
            order_by: params.order_by || "id",
            order_direction: params.order_direction || "asc",
            filter: params.filter || {},
          };
          requests.push(dispatch(fetchServices(serviceParams)));
        }

        // Categories
        if (
          !categoriesFetched &&
          (!categories?.data || categories.data.length === 0)
        ) {
          requests.push(
            dispatch(
              fetchCategory({
                without_pagination: 1,
                filter: { type: "service" },
              }),
            ),
          );
        }

        // Employees
        if (
          !employeesFetched &&
          (!employees?.data || employees.data.length === 0)
        ) {
          requests.push(dispatch(fetchEmployees({ without_pagination: 1 })));
        }

        if (requests.length > 0) {
          await Promise.allSettled(requests);
        }

        setHasInitialized(true);
        console.log("✅ Service hook: Dados carregados!");
      };

      loadData();
    } else if (services?.data && services.data.length > 0) {
      // Se já tem dados (vindos de outro hook), marca como inicializado
      console.log("🔧 Service hook: Dados já disponíveis, pulando requisição");
      setHasInitialized(true);
    }
  }, [
    hasInitialized,
    loading,
    isApiReady,
    services?.data?.length,
    dispatch,
    fetched,
    categoriesFetched,
    employeesFetched,
  ]);

  // Recarregar services quando params mudam (mas só se já inicializou)
  useEffect(() => {
    if (!isApiReady) return;
    if (!hasInitialized) return;
    if (!hasPermission("read service")) return;

    // Garante paginação de 200 por página na tela de serviços
    dispatch(
      fetchServices({
        ...params,
        per_page: 200,
        without_pagination: 0,
      }),
    );
  }, [JSON.stringify(params), isApiReady, hasInitialized, dispatch]);

  // Debounce para busca
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchValue !== params.search) {
        updateParams({
          search: searchValue,
          current_page: 1,
        });
        setCurrentPage(1);
      }
    }, 500); // 500ms de debounce

    return () => clearTimeout(timeoutId);
  }, [searchValue, updateParams, params.search]);

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(serviceSchema),
    defaultValues: {
      category_id: null,
      subscription_id: null,
      name: "",
      description: "",
      return_days: null,
      display_in_agenda: false,
      subscriber_discount: "",
      allow_price_change: false,
      price: "",
      commission: "",
      time: "",
      //string_time: '',
      active: true,
      //points: ''
    },
  });

  // Form para categories
  const {
    register: registerCategory,
    handleSubmit: handleSubmitCategory,
    reset: resetCategory,
    formState: { errors: errorsCategory, isSubmitting: isSubmittingCategory },
  } = useForm({
    resolver: yupResolver(categorySchema),
    defaultValues: {
      name: "",
      model_type: "service",
    },
  });

  const setFormData = useCallback(
    (data: Partial<typeof serviceSchema.__outputType>) => {
      reset(data);
    },
    [reset],
  );

  const setFormDataCategory = useCallback(
    (data: Partial<typeof categorySchema.__outputType>) => {
      resetCategory(data);
    },
    [resetCategory],
  );

  const onSubmit = useCallback(
    async (data: any) => {
      try {
        let result;
        // Normalização de campos antes do envio (JSON ou FormData)
        const normalizePayload = (raw: any, asFormData: boolean) => {
          const booleanKeys = [
            "display_in_agenda",
            "allow_price_change",
            "active",
          ];
          const out: Record<string, any> = {};
          Object.entries(raw).forEach(([key, value]) => {
            if (value === undefined || value === null || value === "") return; // ignora vazios
            if (booleanKeys.includes(key)) {
              const boolVal = !!value; // garante boolean
              out[key] = asFormData ? (boolVal ? "1" : "0") : boolVal; // backend parece esperar true/false ou 1/0; enviamos 1/0 no multipart
              return;
            }
            if (key === "subscriber_discount") {
              const parsed = parseInt(String(value), 10);
              out[key] = isNaN(parsed) ? (asFormData ? "0" : 0) : parsed;
              return;
            }
            if (key === "commission") {
              // Commission pode vir como string percentual; converter para número simples se possível
              const numeric = parseFloat(String(value).replace(",", "."));
              if (!isNaN(numeric)) {
                out[key] = asFormData ? String(numeric) : numeric;
              }
              return;
            }
            // time já vem em HH:MM:SS; manter
            out[key] = value;
          });
          return out;
        };

        if (serviceSlug && hasPermission("update service")) {
          // Atualização: se não houver imagem nova, enviar JSON normal; se houver, multipart
          if (pendingImageFile) {
            const normalized = normalizePayload(data, true);
            const formData = new FormData();
            Object.entries(normalized).forEach(([key, value]) => {
              formData.append(
                key,
                typeof value === "object"
                  ? JSON.stringify(value)
                  : String(value),
              );
            });
            formData.append("image", pendingImageFile);
            result = await dispatch(
              updateService({
                slug: serviceSlug,
                data: formData,
              } as any),
            );
          } else {
            const normalized = normalizePayload(data, false);
            result = await dispatch(
              updateService({
                slug: serviceSlug,
                ...normalized,
              } as UpdateServiceData),
            );
          }
        } else {
          if (hasPermission("create service")) {
            if (pendingImageFile) {
              const normalized = normalizePayload(data, true);
              const formData = new FormData();
              Object.entries(normalized).forEach(([key, value]) => {
                formData.append(
                  key,
                  typeof value === "object"
                    ? JSON.stringify(value)
                    : String(value),
                );
              });
              formData.append("image", pendingImageFile);
              result = await dispatch(createService(formData as any));
            } else {
              const normalized = normalizePayload(data, false);
              result = await dispatch(
                createService(normalized as CreateServiceData),
              );
            }
          }
        }

        if (
          createService.fulfilled.match(result) ||
          updateService.fulfilled.match(result)
        ) {
          // Fecha modal (toggle) apenas uma vez via callback de sucesso
          onSuccess?.();
          // Limpa formulário e estados locais
          reset();
          setServiceSlug(null);
          setPendingImageFile(null);

          // Força refresh após criação/atualização
          console.log("🔄 Refresh após criação/atualização");
          dispatch(resetFetched());
          // Garante paginação de 200 por página na tela de serviços
          await dispatch(
            fetchServices({
              ...params,
              per_page: 200,
              without_pagination: 0,
            }),
          );

          return result.payload;
        }
      } catch (error) {
        console.error("Erro ao enviar dados:", error);
        throw error;
      }
    },
    [serviceSlug, pendingImageFile, params, onSuccess, reset, dispatch],
  );

  const [currentPage, setCurrentPage] = useState(services?.current_page ?? 1);

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
      handlePage(page);
    },
    [handlePage],
  );

  const totalItems = useMemo(() => services?.total ?? 0, [services?.total]);
  const itemsPerPage = useMemo(
    () => services?.per_page ?? 10,
    [services?.per_page],
  );
  const totalPages = useMemo(
    () => (totalItems > 0 ? Math.ceil(totalItems / itemsPerPage) : 1),
    [totalItems, itemsPerPage],
  );
  const pagesArray = useMemo(
    () => Array.from({ length: totalPages }, (_, i) => i + 1),
    [totalPages],
  );

  useEffect(() => {
    if (services?.current_page && services.current_page !== currentPage) {
      setCurrentPage(services.current_page);
    }
  }, [services?.current_page]);

  const handleDelete = useCallback(async () => {
    try {
      if (serviceSlug && hasPermission("delete service")) {
        const result = await dispatch(deleteService(serviceSlug));

        if (deleteService.fulfilled.match(result)) {
          setServiceSlug(null);

          // Força refresh após deleção
          console.log("🗑️ Refresh após deleção");
          dispatch(resetFetched());
          // Garante paginação de 200 por página na tela de serviços
          await dispatch(
            fetchServices({
              ...params,
              per_page: 200,
              without_pagination: 0,
            }),
          );

          return { success: true };
        }

        // Rejeitado - retornar payload para o componente exibir detalhes
        return { success: false, payload: result.payload };
      }

      return { success: false };
    } catch (error) {
      console.error("Error deleting supplier:", error);
      return { success: false, payload: { message: String(error) } };
    }
  }, [serviceSlug, hasPermission, dispatch, params]);

  const getServiceByClient = useCallback(
    async (client: number | string | null, startDateTime?: string) => {
      try {
        client = client === "none" ? null : client;
        const date = {
          client,
          startDateTime,
        };
        await dispatch(fetchServiceByClient(date));
      } catch (error) {
        console.error("Error getting services by client:", error);
      }
    },
    [dispatch],
  );

  const serviceByClient = useMemo(
    () => services?.serviceByClient || [],
    [services?.serviceByClient],
  );

  const handleFilterChange = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  // Category functions
  const onSubmitCategory = useCallback(
    async (data: any) => {
      try {
        const result = categorySlug
          ? await dispatch(updateCategory({ slug: categorySlug, ...data }))
          : await dispatch(createCategory(data));

        if (
          createCategory.fulfilled.match(result) ||
          updateCategory.fulfilled.match(result)
        ) {
          resetCategory();
          setCategorySlug(null);
          dispatch(resetCategoriesFetched());
          await dispatch(
            fetchCategory({
              without_pagination: 1,
              filter: { type: "service" },
            }),
          );
          return true;
        }
      } catch (error) {
        console.error("Category submission error:", error);
      }
      return false;
    },
    [categorySlug, dispatch, resetCategory],
  );

  const handleDeleteCategory = useCallback(async () => {
    try {
      if (categorySlug) {
        await dispatch(deleteCategory(categorySlug));
        setCategorySlug(null);
        dispatch(resetCategoriesFetched());
        await dispatch(
          fetchCategory({
            without_pagination: 1,
            filter: { type: "service" },
          }),
        );
        return true;
      }
    } catch (error) {
      console.error("Error deleting category:", error);
    }
  }, [categorySlug, dispatch]);

  // Category pagination (simplified)
  const currentPageCategory = useMemo(() => 1, []);
  const totalPagesCategory = useMemo(() => 1, []);
  const itemsPerPageCategory = useMemo(() => 10, []);
  const handlePageCategory = useCallback(() => {}, []);
  const handleFilterChangeCategory = useCallback(() => {}, []);

  return {
    // Service functions
    register,
    handleSubmit,
    onSubmit,
    reset,
    errors,
    isSubmitting,
    services,
    categories,
    employees,
    loading,
    error,
    setServiceSlug,
    handleDelete,
    setFormData,
    getValues,
    serviceByClient,
    getServiceByClient,
    control,
    handlePage,
    params,
    currentPage,
    pagesArray,
    handlePageChange,
    currency,
    handleFilterChange,
    searchValue,
    setPendingImageFile,
    pendingImageFile,
    removeExistingImage,
    setRemoveExistingImage,

    // Category functions
    errorsCategory,
    handleSubmitCategory,
    registerCategory,
    onSubmitCategory,
    handleDeleteCategory,
    setCategorySlug,
    setFormDataCategory,
    isSubmittingCategory,
    resetCategory,
    currentPageCategory,
    totalPagesCategory,
    itemsPerPageCategory,
    handlePageCategory,
    handleFilterChangeCategory,
  };
};

export default useService;
